import { useLayoutEffect, useRef, useState } from 'react';

import { Spinner } from './Spinner';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  className?: string;
}

export function Button({ children, variant = 'primary', size = 'md', disabled, loading, type = 'button', onClick, className = '' }: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (loading && buttonRef.current && width === undefined) {
      setWidth(buttonRef.current.offsetWidth);
    } else if (!loading) {
      setWidth(undefined);
    }
  }, [loading, width]);

  let variantClass = '';
  if (variant === 'primary') {
    variantClass = 'bg-sd-warning hover:bg-sd-warning/90 active:bg-sd-warning/80 text-white shadow-sd-sm hover:shadow-sd-md active:shadow-none hover:brightness-110 active:brightness-95';
  } else if (variant === 'secondary') {
    variantClass = 'border border-sd-primary text-sd-primary hover:bg-sd-primary-subtle active:bg-sd-primary-subtle/80 hover:border-sd-primary/70';
  } else if (variant === 'ghost') {
    variantClass = 'text-sd-text-secondary hover:bg-sd-surface-elevated active:bg-sd-surface-elevated/80';
  } else if (variant === 'danger') {
    variantClass = 'border border-sd-error text-sd-error hover:bg-sd-error/10 active:bg-sd-error/20';
  }

  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';

  let sizeClass = '';
  if (size === 'sm') {
    sizeClass = 'px-3 py-1.5 min-h-[44px] text-sd-sm';
  } else if (size === 'md') {
    sizeClass = 'px-4 py-2 min-h-[44px] text-sd-base';
  } else if (size === 'lg') {
    sizeClass = 'px-6 py-3 min-h-[44px] text-sd-lg';
  }

  const focusClasses = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1';
  const transitionClasses = 'transition-all duration-200 ease-out';
  const roundedClass = 'rounded-sd-md';

  return (
    <button ref={buttonRef} className={`${variantClass} ${sizeClass} ${disabledClass} ${focusClasses} ${transitionClasses} ${roundedClass} ${className}`.trim()} type={type} disabled={disabled || loading} onClick={loading ? undefined : onClick} style={width !== undefined ? { minWidth: `${width}px` } : undefined}>
      {loading ? <span className="sd-fade-in"><Spinner size="sm" /></span> : children}
    </button>
  );
}
